import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SeoContent from '../components/SeoContent';
import PageSEO from '../components/PageSEO';

const VOWELS = new Set('aeiou');

function toPigLatin(text) {
  return text.replace(/\b([a-zA-Z]+)\b/g, (word) => {
    const lower = word.toLowerCase();
    const isUpper = word === word.toUpperCase();
    const isTitle = word[0] === word[0].toUpperCase() && word.slice(1) === word.slice(1).toLowerCase();

    let result;
    if (VOWELS.has(lower[0])) {
      result = lower + 'way';
    } else {
      let i = 0;
      while (i < lower.length && !VOWELS.has(lower[i])) i++;
      if (i === lower.length) {
        result = lower + 'ay';
      } else {
        result = lower.slice(i) + lower.slice(0, i) + 'ay';
      }
    }

    if (isUpper) return result.toUpperCase();
    if (isTitle) return result.charAt(0).toUpperCase() + result.slice(1);
    return result;
  });
}

function fromPigLatin(text) {
  return text.replace(/\b([a-zA-Z]+)way\b/g, (_, w) => w)
             .replace(/\b([a-zA-Z]+)ay\b/g, (_, w) => {
               const vowels = 'aeiou';
               for (let i = 1; i < w.length; i++) {
                 if (vowels.includes(w[i].toLowerCase())) {
                   return w.slice(i) + w.slice(0, i);
                 }
               }
               return w;
             });
}

const EXAMPLES = [
  'Hello World',
  'The quick brown fox',
  'I love word games',
  'Spelling is fun',
];

export default function PigLatinConverter() {
  const [input, setInput]   = useState('');
  const [mode, setMode]     = useState('to');

  const output = input
    ? (mode === 'to' ? toPigLatin(input) : fromPigLatin(input))
    : '';

  function copyOutput() {
    if (output) navigator.clipboard.writeText(output);
  }

  return (
    <div className="game-page">
      <PageSEO page="pig-latin" />
      <div className="container" style={{maxWidth:700}}>
        <Link to="/" className="back-btn"><i className="bi bi-arrow-left"></i> All Games</Link>

        <div className="game-header">
          <span className="game-icon">🐷</span>
          <h1>Pig Latin Converter</h1>
          <p>Convert any English text to Pig Latin — or translate it back instantly!</p>
        </div>

        <div className="solver-card">
          <div className="d-flex gap-2 mb-3">
            <button type="button" className="btn fw-bold flex-fill"
              style={{borderRadius:50, fontFamily:'Nunito,sans-serif',
                background: mode === 'to' ? 'var(--primary)' : 'var(--border)',
                color: mode === 'to' ? '#fff' : 'var(--text)', border:'none'}}
              onClick={() => setMode('to')}>
              English → Pig Latin
            </button>
            <button type="button" className="btn fw-bold flex-fill"
              style={{borderRadius:50, fontFamily:'Nunito,sans-serif',
                background: mode === 'from' ? 'var(--primary)' : 'var(--border)',
                color: mode === 'from' ? '#fff' : 'var(--text)', border:'none'}}
              onClick={() => setMode('from')}>
              Pig Latin → English
            </button>
          </div>

          <div className="mb-3">
            <label className="fw-bold mb-1">{mode === 'to' ? 'English Text' : 'Pig Latin Text'}</label>
            <textarea
              className="form-control"
              rows={3}
              style={{fontFamily:'Nunito,sans-serif', fontSize:'1.1rem', borderRadius:12}}
              value={input}
              onChange={e => setInput(e.target.value)}
              placeholder={mode === 'to' ? 'Type any English text here…' : 'Type Pig Latin text here…'}
            />
          </div>

          <div className="fw-bold text-muted small mb-1">Quick examples:</div>
          <div className="d-flex flex-wrap gap-2 mb-3">
            {EXAMPLES.map(ex => (
              <button key={ex} type="button" className="btn btn-sm fw-bold"
                style={{borderRadius:50, background:'var(--border)', border:'none', fontFamily:'Nunito,sans-serif'}}
                onClick={() => { setInput(ex); setMode('to'); }}>
                {ex}
              </button>
            ))}
          </div>
        </div>

        {output && (
          <div className="solver-card">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <div className="fw-bold">{mode === 'to' ? '🐷 Pig Latin Result' : '📝 English Result'}</div>
              <button className="btn btn-sm fw-bold" onClick={copyOutput}
                style={{borderRadius:50, background:'var(--border)', border:'none', fontFamily:'Nunito,sans-serif'}}>
                📋 Copy
              </button>
            </div>
            <div className="p-3 rounded-3" style={{background:'var(--surface)', fontSize:'1.2rem', fontFamily:'Nunito,sans-serif', lineHeight:1.8}}>
              {output}
            </div>
            {mode === 'to' && (
              <div className="mt-3 p-3 rounded-3" style={{background:'#f0fdf4', borderLeft:'4px solid #16a34a'}}>
                <div className="fw-bold small text-success mb-1">How Pig Latin Works:</div>
                <ul className="mb-0 small text-muted">
                  <li>Words starting with a <strong>vowel</strong>: add <strong>"way"</strong> → <em>apple</em> → <em>appleway</em></li>
                  <li>Words starting with <strong>consonants</strong>: move them to end + <strong>"ay"</strong> → <em>string</em> → <em>ingstray</em></li>
                </ul>
              </div>
            )}
          </div>
        )}
        <SeoContent game="pig-latin" />
      </div>
    </div>
  );
}
